import { create } from 'zustand';
import { Product } from 'shopify-storefront-api-typings';
import { ProductConnectionWithFilters } from '@app/lib/shopify/types/Collection';
import { ShopifyFilter } from '@app/lib/shopify/types/Filter';
import { Sort } from '@app/[lang]/collections/collection-filtering-toolbar/types';
import DefaultCollectionFilters from '@app/lib/shopify/filters/default-collection-filters';
import addQueryParams from '@app/lib/urls/add-query-params';
import { SORT_MAP } from './managed-filters-drawer';
import invert from 'lodash.invert';
import { filtersToFromUrlQuery } from '@app/[lang]/collections/utilities/filters-to-from-search-query';
import qs from 'qs';

interface CollectionState {
    products: Product[];
    productCount: number;
    productCursor: string | null;
    hasNextPage: boolean;
    selectedFilters: ShopifyFilter[];
    appliedFilters: any[];
    selectedSort: Sort | null;
    availableFilters: any[];
    isStateInitialised: boolean;
    filterView: 'product_count' | 'active_filters';
    currentCollectionHandle: string | null;
    setProducts: (productConnection: ProductConnectionWithFilters) => void;
    addProducts: (productConnection: ProductConnectionWithFilters) => void;
    setSelectedFilters: (filters: ShopifyFilter[]) => void;
    setSelectedSort: (sort: Sort) => void;
    setCurrentCollectionHandle: (handle: string) => void;
    initialiseFromQueryParams: () => void;
    resetFilters: () => void;
}

const FILTER_VIEW_PRODUCT_COUNT = 'product_count';

const useCollection = create<CollectionState>((set, get) => ({
    products: [],
    productCount: 0,
    productCursor: null,
    hasNextPage: false,
    selectedFilters: [],
    appliedFilters: [],
    selectedSort: null,
    availableFilters: [],
    filterView: FILTER_VIEW_PRODUCT_COUNT,
    isStateInitialised: false,
    currentCollectionHandle: null,
    setProducts: (productConnection: ProductConnectionWithFilters) => {
        set({
            products: productConnection.edges.map(
                (productEdge) => productEdge.node
            ),
            productCount: productConnection.edges.length,
            productCursor: productConnection.edges?.slice(-1)[0]?.cursor,
            hasNextPage: productConnection.pageInfo.hasNextPage,
            availableFilters: productConnection.filters,
            isStateInitialised: true,
        });
    },
    addProducts: (productConnection: ProductConnectionWithFilters) => {
        set({
            products: [
                ...get().products,
                ...productConnection.edges.map(
                    (productEdge) => productEdge.node
                ),
            ],
            productCount:
                get().products.length + productConnection.edges.length,
            productCursor: productConnection?.edges.slice(-1)[0]?.cursor,
            hasNextPage: productConnection.pageInfo.hasNextPage,
            availableFilters: productConnection.filters,
        });
    },
    initialiseFromQueryParams: () => {
        const updatedState: any = {};
        const urlParams = qs.parse(window.location.search, {
            ignoreQueryPrefix: true,
        });
        const { sort, ...urlParamsSansSort } = urlParams;
        const mappedFilters = filtersToFromUrlQuery(
            Object.entries(urlParamsSansSort),
            true
        );

        if (mappedFilters.length > 0) {
            const appliedFilters = [
                ...DefaultCollectionFilters,
                ...mappedFilters.map((filter: ShopifyFilter) =>
                    JSON.parse(filter?.input)
                ),
                { available: true },
            ];
            updatedState.selectedFilters = mappedFilters;
            updatedState.appliedFilters = appliedFilters;
        }

        if (urlParams.sort) {
            const sortParams = urlParams.sort as string;
            const id = invert(SORT_MAP)[sortParams];
            const [sortKey] = id.split('-');
            const reverse = id.includes('desc') ? true : false;

            const sort: Sort = {
                id,
                sortKey: sortKey.toUpperCase(),
                reverse,
            };
            updatedState.selectedSort = sort;
        }

        set(updatedState);
    },
    setSelectedSort: (sort: Sort) => {
        addQueryParams({
            ...filtersToFromUrlQuery(get().selectedFilters),
            sort: SORT_MAP[sort.id],
        });

        if (sort.id === 'default') {
            sort.sortKey = null;
        }

        set({ selectedSort: sort });
    },
    setSelectedFilters: (filters: ShopifyFilter[]) => {
        const appliedFilters = [
            ...DefaultCollectionFilters,
            ...filters.map((filter: ShopifyFilter) =>
                JSON.parse(filter?.input)
            ),
        ];

        if (filters.length > 0) {
            appliedFilters.push({ available: true });
        }

        const params = {
            ...filtersToFromUrlQuery(filters),
        };
        if (get().selectedSort !== null) {
            params.sort = SORT_MAP[get().selectedSort!.id];
        }

        addQueryParams(params);

        set({
            selectedFilters: filters,
            appliedFilters: appliedFilters,
        });
    },
    setCurrentCollectionHandle: (handle: string) => {
        set({ currentCollectionHandle: handle });
    },

    resetFilters: () => {
        set({
            selectedFilters: [],
            appliedFilters: [...DefaultCollectionFilters],
            selectedSort: null,
        });
    },
}));

export default useCollection;
